import type { Option } from "../../constants";
import type {
	DatabaseTableOptionV2,
	EntityOption,
	ResourceCategoryV2,
	ScreenLocationFieldOption,
	VariableGroupV2,
	VisibleGroupKey,
} from "./types";

/** Keeps the first occurrence of each `value` (value-field global constants can repeat from upstream data). */
export function dedupeOptionsByValue(options: Option[]): Option[] {
	const seen = new Set<string>();
	const out: Option[] = [];
	for (const opt of options) {
		if (seen.has(opt.label)) continue;
		seen.add(opt.label);
		out.push(opt);
	}
	return out;
}

function filterOptionsByQuery(
	options: Option[],
	q: string,
): Option[] {
	if (!q) return options;
	return options.filter((o) => o.label.toLowerCase().includes(q));
}

function filterTablesByQuery(
	tables: DatabaseTableOptionV2[],
	q: string,
): DatabaseTableOptionV2[] {
	if (!q) return tables;
	return tables.filter(
		(t) =>
			t.label.toLowerCase().includes(q) ||
			t.fields.some((f) => f.label.toLowerCase().includes(q)),
	);
}

function filterScreenLocationsByQuery(
	locations: ScreenLocationFieldOption[],
	q: string,
	cachedFieldsByLocationId: Record<string, Option[]>,
): ScreenLocationFieldOption[] {
	if (!q) return locations;
	return locations.filter((loc) => {
		if (loc.name.toLowerCase().includes(q)) return true;
		const cached = cachedFieldsByLocationId[String(loc.id)] ?? loc.fields ?? [];
		return cached.some((f) => f.label.toLowerCase().includes(q));
	});
}

/** MUI Autocomplete keys options by label by default — same-named screen locations need id-based keys. */
export function entityOptionKey(option: EntityOption | string): string {
	if (typeof option === "string") return option;
	switch (option.kind) {
		case "field":
			return `field:${option.groupKey}:${option.option.value}`;
		case "table":
			return `table:${option.groupKey}:${option.table.value}`;
		case "screenLocation":
			return `screenLocation:${option.groupKey}:${option.location.id}`;
		case "showMore":
			return `showMore:${option.groupKey}`;
		default:
			return "";
	}
}

function appendFieldOptions(
	out: EntityOption[],
	categoryKey: VisibleGroupKey,
	options: Option[],
	category: ResourceCategoryV2,
	groupKey: VisibleGroupKey,
	q: string,
	expandedGroups: Set<VisibleGroupKey>,
	maxVisibleItems: number,
): void {
	const filtered = filterOptionsByQuery(options, q);
	if (filtered.length === 0) return;

	const isExpanded = expandedGroups.has(categoryKey);
	const visible = isExpanded ? filtered : filtered.slice(0, maxVisibleItems);
	const remaining = filtered.length - maxVisibleItems;

	for (const option of visible) {
		out.push({ kind: "field", option, category, groupKey });
	}
	if (!isExpanded && remaining > 0) {
		out.push({ kind: "showMore", groupKey: categoryKey, remaining });
	}
}

function appendTableOptions(
	out: EntityOption[],
	categoryKey: VisibleGroupKey,
	tables: DatabaseTableOptionV2[],
	groupKey: VisibleGroupKey,
	q: string,
	expandedGroups: Set<VisibleGroupKey>,
	maxVisibleItems: number,
): void {
	const filtered = filterTablesByQuery(tables, q);
	if (filtered.length === 0) return;

	const isExpanded = expandedGroups.has(categoryKey);
	const visible = isExpanded ? filtered : filtered.slice(0, maxVisibleItems);
	const remaining = filtered.length - maxVisibleItems;

	for (const table of visible) {
		out.push({ kind: "table", table, groupKey });
	}
	if (!isExpanded && remaining > 0) {
		out.push({ kind: "showMore", groupKey: categoryKey, remaining });
	}
}

function appendScreenLocationOptions(
	out: EntityOption[],
	categoryKey: VisibleGroupKey,
	locations: ScreenLocationFieldOption[],
	groupKey: VisibleGroupKey,
	q: string,
	expandedGroups: Set<VisibleGroupKey>,
	maxVisibleItems: number,
	cachedFieldsByLocationId: Record<string, Option[]>,
): void {
	const filtered = filterScreenLocationsByQuery(locations, q, cachedFieldsByLocationId);
	if (filtered.length === 0) return;

	const isExpanded = expandedGroups.has(categoryKey);
	const visible = isExpanded ? filtered : filtered.slice(0, maxVisibleItems);
	const remaining = filtered.length - maxVisibleItems;

	for (const location of visible) {
		out.push({ kind: "screenLocation", location, groupKey });
	}
	if (!isExpanded && remaining > 0) {
		out.push({ kind: "showMore", groupKey: categoryKey, remaining });
	}
}

export function buildMainOptions(
	effectiveGroups: VisibleGroupKey[],
	q: string,
	expandedGroups: Set<VisibleGroupKey>,
	maxVisibleItems: number,
	screenFields: Option[],
	advancedTriggers: Option[],
	collections: Option[],
	currentCollectionOptions: Option[],
	otherCollectionOptions: Option[],
	variableGroups: VariableGroupV2[],
	formulaVariables: Option[],
	databaseSingleRowVariableOptions: Option[],
	singleRowDbVariables: DatabaseTableOptionV2[],
	multiRowDbVariables: DatabaseTableOptionV2[],
	screenTables: DatabaseTableOptionV2[],
	globalConstants: Option[],
	reactionResources: Option[],
	otherScreenLocations: ScreenLocationFieldOption[],
	suggestedScreenLocations: ScreenLocationFieldOption[],
	cachedFieldsByLocationId: Record<string, Option[]>,
): EntityOption[] {
	const out: EntityOption[] = [];

	for (const groupKey of effectiveGroups) {
		switch (groupKey) {
			case "screenFields":
				appendFieldOptions(
					out,
					"screenFields",
					screenFields,
					"screenField",
					groupKey,
					q,
					expandedGroups,
					maxVisibleItems,
				);
				break;
			case "advancedTriggers":
				appendFieldOptions(
					out,
					"advancedTriggers",
					advancedTriggers,
					"advancedTrigger",
					groupKey,
					q,
					expandedGroups,
					maxVisibleItems,
				);
				break;
			case "collections":
				appendFieldOptions(
					out,
					"collections",
					collections,
					"collection",
					groupKey,
					q,
					expandedGroups,
					maxVisibleItems,
				);
				break;
			case "currentCollection":
				appendFieldOptions(
					out,
					"currentCollection",
					currentCollectionOptions,
					"collection",
					groupKey,
					q,
					expandedGroups,
					maxVisibleItems,
				);
				break;
			case "otherCollections":
				appendFieldOptions(
					out,
					"otherCollections",
					otherCollectionOptions,
					"collection",
					groupKey,
					q,
					expandedGroups,
					maxVisibleItems,
				);
				break;
			case "suggestedFields":
				appendScreenLocationOptions(
					out,
					"suggestedFields",
					suggestedScreenLocations,
					groupKey,
					q,
					expandedGroups,
					maxVisibleItems,
					cachedFieldsByLocationId,
				);
				break;
			case "simpleVariable": {
				const allSimpleOptions = variableGroups.flatMap((g) => g.options);
				appendFieldOptions(
					out,
					"simpleVariable",
					allSimpleOptions,
					"simpleVariable",
					groupKey,
					q,
					expandedGroups,
					maxVisibleItems,
				);
				break;
			}
			case "formulaVariable":
				appendFieldOptions(
					out,
					"formulaVariable",
					formulaVariables,
					"formulaVariable",
					groupKey,
					q,
					expandedGroups,
					maxVisibleItems,
				);
				break;
			case "singleRowDatabaseVariables":
				if (singleRowDbVariables.length > 0) {
					appendTableOptions(
						out,
						"singleRowDatabaseVariables",
						singleRowDbVariables,
						groupKey,
						q,
						expandedGroups,
						maxVisibleItems,
					);
				} else {
					appendFieldOptions(
						out,
						"singleRowDatabaseVariables",
						databaseSingleRowVariableOptions,
						"databaseSingleRowVariable",
						groupKey,
						q,
						expandedGroups,
						maxVisibleItems,
					);
				}
				break;
			case "multiRowDatabaseVariables":
				appendTableOptions(
					out,
					"multiRowDatabaseVariables",
					multiRowDbVariables,
					groupKey,
					q,
					expandedGroups,
					maxVisibleItems,
				);
				break;
			case "screenTables":
				appendTableOptions(
					out,
					"screenTables",
					screenTables,
					groupKey,
					q,
					expandedGroups,
					maxVisibleItems,
				);
				break;
			case "otherScreenLocations":
				appendScreenLocationOptions(
					out,
					"otherScreenLocations",
					otherScreenLocations,
					groupKey,
					q,
					expandedGroups,
					maxVisibleItems,
					cachedFieldsByLocationId,
				);
				break;
			case "globalConstants":
				appendFieldOptions(
					out,
					"globalConstants",
					globalConstants,
					"globalConstant",
					groupKey,
					q,
					expandedGroups,
					maxVisibleItems,
				);
				break;
			case "reactions":
				appendFieldOptions(
					out,
					"reactions",
					reactionResources,
					"reactionResource",
					groupKey,
					q,
					expandedGroups,
					maxVisibleItems,
				);
				break;
		}
	}

	return out;
}
